import React, { useEffect, useState } from "react";
import { admin } from "../../lib/api";

export default function FixtureBench() {
  const [fixtures, setFixtures] = useState([]);
  const [busy, setBusy] = useState(null);
  const [results, setResults] = useState({});
  const [running, setRunning] = useState(false);
  const [batchResult, setBatchResult] = useState(null);
  const [freeze, setFreeze] = useState(null);
  const [signer, setSigner] = useState("");
  const [signing, setSigning] = useState(false);
  const [signError, setSignError] = useState(null);

  useEffect(() => {
    (async () => {
      const res = await admin.fixtures();
      setFixtures(res.fixtures);
      try {
        const f = await admin.templateFreeze();
        setFreeze(f.freeze || null);
      } catch { /* no freeze yet */ }
    })();
  }, []);

  const run = async (fx, step) => {
    setBusy(`${fx}-${step}`);
    try {
      const res = await admin.runFixture(fx, step);
      setResults((r) => ({ ...r, [fx]: res }));
    } finally { setBusy(null); }
  };

  const signFreeze = async () => {
    if (!signer.trim()) {
      setSignError("Type your name to sign the freeze.");
      return;
    }
    setSignError(null);
    setSigning(true);
    try {
      const res = await admin.signTemplate(signer.trim(), "v1.0");
      setFreeze(res.freeze);
      setBatchResult(null);
    } catch (e) {
      const detail = e?.response?.data?.detail;
      if (detail && typeof detail === "object") {
        setSignError(
          `${detail.message} (${detail.failing?.length || 0} fixture(s) failing). Run the regression suite for details.`
        );
      } else {
        setSignError(String(detail || e.message || e));
      }
    } finally { setSigning(false); }
  };

  const allReady = batchResult?.all_ready;
  const readyCount = batchResult?.template_v1_ready_count ?? 0;
  const totalCount = batchResult?.total ?? 0;

  return (
    <div data-testid="fixture-bench-page">
      <div className="cw-eyebrow">Fixture Bench</div>
      <h1 className="cw-display-l" style={{ marginTop: 8 }}>Reference personas.</h1>
      <p className="cw-body" style={{ marginTop: 8, color: "rgba(22,30,27,0.72)" }}>
        Deterministic personas used to validate scoring, block selection, and PDF rendering end-to-end.
        F1&ndash;F5 are the reference set; F6&ndash;F9 exercise minimum states, no-flags, integrity (R1/R4/R6), and contradictions (R3).
      </p>

      {/* L5 Template Freeze panel */}
      <div
        className="cw-card"
        data-testid="template-freeze-panel"
        style={{
          marginTop: 24, padding: 20,
          background: freeze?.frozen ? "rgba(56,207,177,0.08)" : "#FFFFFF",
          border: freeze?.frozen ? "1px solid rgba(56,207,177,0.45)" : "1px solid var(--cw-border-light)",
        }}
      >
        <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 16, flexWrap: "wrap" }}>
          <div style={{ minWidth: 240 }}>
            <div className="cw-eyebrow">Template chassis</div>
            <div className="cw-heading-m" style={{ marginTop: 4 }}>
              {freeze?.frozen ? `Frozen at ${freeze.template_version}` : "Template v1.0 not yet frozen"}
            </div>
            {freeze?.frozen && (
              <div className="cw-small" style={{ marginTop: 6, color: "rgba(22,30,27,0.72)" }}>
                Signed by <strong>{freeze.signer}</strong> · {freeze.signed_at?.slice(0, 19).replace("T", " ")} UTC
                <br />Fixtures hash <span className="cw-tabular">{freeze.fixtures_hash}</span> ·
                Wordmark <span className="cw-tabular">{freeze.wordmark_width_mm}mm</span>
              </div>
            )}
            {!freeze?.frozen && (
              <div className="cw-small" style={{ marginTop: 6, color: "rgba(22,30,27,0.72)" }}>
                Sign the freeze when every fixture passes every guard: syntax, band, verbatim, ampersand, and wordmark.
              </div>
            )}
          </div>

          {!freeze?.frozen && (
            <div style={{ display: "flex", flexDirection: "column", gap: 8, minWidth: 260 }}>
              <input
                className="cw-input light"
                data-testid="freeze-signer-input"
                value={signer}
                onChange={(e) => setSigner(e.target.value)}
                placeholder="Your full name"
                style={{ height: 44 }}
              />
              <button
                className="cw-btn light-primary"
                onClick={signFreeze}
                disabled={signing}
                data-testid="sign-freeze-btn"
                style={{ height: 44, padding: "0 22px" }}
              >
                {signing ? "Signing\u2026 running full regression" : "Sign & freeze template v1.0"}
              </button>
              {signError && (
                <div className="cw-small" data-testid="freeze-error" style={{ color: "#B04A47" }}>
                  {signError}
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      <div style={{ marginTop: 20, display: "flex", gap: 12, alignItems: "center", flexWrap: "wrap" }}>
        <button
          className="cw-btn light-primary"
          onClick={async () => {
            if (running) return;
            setRunning(true);
            setBatchResult(null);
            try {
              const res = await admin.runAllFixtures();
              setBatchResult(res);
            } catch (e) {
              setBatchResult({ error: String(e) });
            } finally { setRunning(false); }
          }}
          disabled={running}
          data-testid="run-all-fixtures-btn"
          style={{ height: 44, padding: "0 22px" }}
        >{running ? "Running all fixtures\u2026 this takes 3\u20135 minutes" : "Run all fixtures (regression suite)"}</button>
        {batchResult && !batchResult.error && (
          <>
            <span className="pill pill-seafoam">
              {batchResult.ok}/{batchResult.total} rendered clean
            </span>
            <span
              className={`pill ${allReady ? "pill-seafoam" : "pill-marigold"}`}
              data-testid="template-ready-pill"
            >
              {readyCount}/{totalCount} template-v1 ready
            </span>
          </>
        )}
        {batchResult?.error && (
          <span className="pill pill-marigold">Error: {batchResult.error}</span>
        )}
      </div>

      {batchResult?.results && (
        <div className="cw-card" style={{ marginTop: 20, padding: 0, overflowX: "auto" }}>
          <table className="cw-table">
            <thead><tr><th>ID</th><th>Band</th><th>Tier</th><th>Flags</th><th>em/en</th><th>Guards</th><th></th></tr></thead>
            <tbody>
              {batchResult.results.map((r) => {
                const guards = r.guards?.guards || {};
                const offenceKeys = Object.keys(guards).filter((k) => (guards[k] || []).length > 0);
                return (
                  <tr key={r.fixture_id}>
                    <td><strong>{r.fixture_id}</strong></td>
                    <td>{r.band}</td>
                    <td>{r.tier}</td>
                    <td>{r.flag_count}</td>
                    <td className="cw-tabular">{r.em_dashes} / {r.en_dashes}</td>
                    <td>
                      {r.template_v1_ready ? (
                        <span className="pill pill-seafoam" data-testid={`guards-pass-${r.fixture_id}`}>pass</span>
                      ) : (
                        <span
                          className="pill pill-marigold"
                          data-testid={`guards-fail-${r.fixture_id}`}
                          title={offenceKeys.map((k) => `${k}: ${guards[k].join("; ")}`).join(" | ")}
                        >
                          fail: {offenceKeys.join(", ")}
                        </span>
                      )}
                    </td>
                    <td>{r.report_id && <a className="cw-btn-text" href={admin.pdfUrl(r.report_id)} target="_blank" rel="noopener noreferrer">PDF</a>}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      <div style={{ marginTop: 32, display: "grid", gap: 16 }}>
        {fixtures.map((f) => {
          const r = results[f.id];
          return (
            <div key={f.id} className="cw-card" data-testid={`fixture-${f.id}`}>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 16, flexWrap: "wrap" }}>
                <div>
                  <div className="cw-heading-m">{f.label}</div>
                  <div className="cw-small" style={{ marginTop: 4 }}>{f.sector} · {f.country}</div>
                </div>
                <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                  <button
                    className="cw-btn light-primary"
                    style={{ height: 40, padding: "0 16px" }}
                    onClick={() => run(f.id, "scoring")}
                    disabled={busy === `${f.id}-scoring`}
                    data-testid={`run-scoring-${f.id}`}
                  >{busy === `${f.id}-scoring` ? "Running…" : "Run scoring"}</button>
                  <button
                    className="cw-btn light-primary"
                    style={{ height: 40, padding: "0 16px" }}
                    onClick={() => run(f.id, "all")}
                    disabled={busy === `${f.id}-all`}
                    data-testid={`generate-pdf-${f.id}`}
                  >{busy === `${f.id}-all` ? "Generating…" : "Generate PDF"}</button>
                </div>
              </div>

              {r && (
                <div style={{ marginTop: 20, borderTop: "1px solid var(--cw-border-light)", paddingTop: 16 }}>
                  <div className="cw-small">
                    Readiness: <strong>{r.scoring.readiness_band}</strong> ({r.scoring.readiness_points} pts) ·
                    Gating: <strong>{r.scoring.gating_constraint}</strong> ·
                    Lead score: <strong>{r.scoring.lead_score}</strong> ({r.scoring.lead_tier}) ·
                    Flags: {r.scoring.exposure_flag_ids.length}
                  </div>
                  <div className="cw-small" style={{ marginTop: 8 }}>
                    Top opportunities: {r.scoring.opportunity_ranking.slice(0, 3).map((o) => o.name).join(" · ")}
                  </div>
                  {r.report_id && (
                    <div style={{ marginTop: 12, display: "flex", gap: 12, alignItems: "center" }}>
                      <a
                        className="cw-btn light-primary"
                        style={{ height: 40, padding: "0 16px" }}
                        href={admin.pdfUrl(r.report_id)}
                        target="_blank" rel="noopener noreferrer"
                        data-testid={`download-pdf-${f.id}`}
                      >Download PDF</a>
                      {r.regulator_leakage && r.regulator_leakage.length > 0 && (
                        <span className="pill pill-marigold" data-testid={`leakage-${f.id}`}>
                          Regulator leakage detected: {r.regulator_leakage.join(", ")}
                        </span>
                      )}
                      {r.regulator_leakage && r.regulator_leakage.length === 0 && (
                        <span className="pill pill-seafoam" data-testid={`no-leakage-${f.id}`}>Jurisdiction-clean</span>
                      )}
                    </div>
                  )}
                  {r.executive_summary_preview && (
                    <div style={{ marginTop: 16, padding: 16, background: "rgba(22,30,27,0.03)", borderRadius: 12 }}>
                      <div className="cw-eyebrow">Executive summary preview</div>
                      <p className="cw-body" style={{ marginTop: 8, whiteSpace: "pre-wrap" }}>
                        {r.executive_summary_preview}
                      </p>
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
